import Component from '@glimmer/component';
import { service } from '@ember/service';
import { concat } from '@ember/helper';
import TooltipButton from 'tio-ui/components/tooltip-button';
import {
  PhSparkle,
  PhChatCenteredText,
  PhBuilding,
  PhCurrencyCircleDollar,
  PhFile,
  PhCalendarDots,
  PhStudent,
  PhCreditCard,
} from 'ember-phosphor-icons';
import type IntlService from 'ember-intl/services/intl';
import type { SafeString } from '@ember/template';
import type { ComponentLike } from '@glint/template';
import type FeaturesService from '../../../services/features';

/** A reference drawer the approval page can show beside the application. */
export type ReferenceDrawer =
  | 'ai-analysis'
  | 'comments'
  | 'course-details'
  | 'employer-info'
  | 'financial-details'
  | 'attachments'
  | 'timeline'
  | 'course-cash';

interface ReferenceControl {
  drawer: ReferenceDrawer;
  label: string | SafeString;
  icon: ComponentLike<{ Args: { size?: number | string }; Element: SVGElement }>;
  /** Suffix of the `data-test-button` hook, kept from the original footer buttons. */
  testName: string;
}

interface ReferenceControlsSignature {
  Element: HTMLDivElement;
  Args: {
    /** The open drawer, shown pressed; null or undefined when none is open. */
    activeDrawer?: string | null;
    /** Opens the drawer, or closes it when it is already the open one. */
    onToggle: (drawer: ReferenceDrawer) => void;
    /** The `?ai=true` query param; production admin sessions show AI analysis only with it. */
    ai?: boolean;
    /** Program label for the course list, e.g. "Courses". */
    expenseType?: string;
    /** Shows the admin-only CourseCash transactions button. */
    hasCourseCash?: boolean;
    /** Stacks the buttons for the right-edge rail, with tooltips opening to the left. */
    vertical?: boolean;
  };
}

/** The approval page's reference drawer buttons, shared by the bottom action bar and the right rail. */
export default class ReferenceControls extends Component<ReferenceControlsSignature> {
  @service declare features: FeaturesService;
  @service declare intl: IntlService;

  get testPrefix() {
    return this.args.vertical ? 'rail' : 'footer';
  }

  get showAiAnalysis() {
    if (!this.features.isAdminApp || !this.features.isEnabled('aiAnalysis')) return false;
    return this.features.tioEnvironment !== 'production' || this.args.ai === true;
  }

  get controls() {
    const controls: ReferenceControl[] = [
      {
        drawer: 'comments',
        label: this.intl.t('tuition_assistance.application_approval.comments'),
        icon: PhChatCenteredText,
        testName: 'comments',
      },
      {
        drawer: 'course-details',
        label: this.args.expenseType || this.intl.t('tuition_assistance.courses'),
        icon: PhStudent,
        testName: 'course-details',
      },
      {
        drawer: 'employer-info',
        label: this.intl.t('tuition_assistance.application_approval.partner_information'),
        icon: PhBuilding,
        testName: 'partner-info',
      },
      {
        drawer: 'financial-details',
        label: this.intl.t('tuition_assistance.application_approval.financial_details'),
        icon: PhCurrencyCircleDollar,
        testName: 'financial-details',
      },
      {
        drawer: 'attachments',
        label: this.intl.t('tuition_assistance.application_approval.attachments'),
        icon: PhFile,
        testName: 'attachments',
      },
      {
        drawer: 'timeline',
        label: this.intl.t('tuition_assistance.application_approval.timeline'),
        icon: PhCalendarDots,
        testName: 'timeline',
      },
    ];
    if (this.showAiAnalysis) {
      controls.unshift({
        drawer: 'ai-analysis',
        label: this.intl.t('tuition_assistance.application_approval.ai_analysis'),
        icon: PhSparkle,
        testName: 'ai-analysis',
      });
    }
    if (this.args.hasCourseCash) {
      controls.push({
        drawer: 'course-cash',
        // The brand mark is a SafeString so ember-intl leaves its markup unescaped.
        label: this.intl.t('common.tas.course_cash.transactions_tooltip', {
          htmlSafe: true,
          courseCash: this.intl.t('common.tas.course_cash.default', { htmlSafe: true }),
        }),
        icon: PhCreditCard,
        testName: 'course-cash',
      });
    }
    return controls;
  }

  isActive = (drawer: ReferenceDrawer) => this.args.activeDrawer === drawer;

  <template>
    <div
      class={{if
        @vertical
        "tio-col-tight items-center"
        "tio-row-extra-tight justify-around md:justify-end md:gap-1"
      }}
      ...attributes
    >
      {{#each this.controls key="drawer" as |control|}}
        <TooltipButton
          @tooltip={{control.label}}
          @tooltipPlacement={{if @vertical "left" "top"}}
          @appearance="minimal"
          @intent="default"
          @size="sm"
          @isActive={{this.isActive control.drawer}}
          class="!p-2 !rounded-md"
          aria-expanded={{if (this.isActive control.drawer) "true" "false"}}
          data-test-button={{concat this.testPrefix "-" control.testName "-button"}}
          {{on "click" (fn @onToggle control.drawer)}}
        >
          {{#if @vertical}}
            <control.icon @size="24" />
          {{else}}
            <control.icon @size="20" class="md:hidden" />
            <control.icon @size="24" class="hidden md:block" />
          {{/if}}
        </TooltipButton>
      {{/each}}
    </div>
  </template>
}
